import { useEffect, useMemo, useRef } from 'react';
import { FilterPasteModal } from '../../../../../../../components/FilterSection';
import { useOfferFormContext } from '../../context';
import type { ProdutoDetalhado } from '../../hooks/useProdutosCampanha';
import { useProdutosCampanha } from '../../hooks/useProdutosCampanha';
import type { OfferFormState } from '../../types/offer.types';
import { validateOffer } from '../../utils/offerValidation';
import styles from './OfferCreationForm.module.scss';
import { OfferSettingsSection } from './OfferSettingsSection/OfferSettingsSection';
import { ProductSearchSection } from './ProductSearchSection/ProductSearchSection';
import { ProductsTableSection } from './ProductsTableSection/ProductsTableSection';

interface OfferCreationFormProps {
  idsLojas: string[];
  blocosOfertas: any[];
  setOferta: (data: any) => void;
  editOferta?: boolean;
  id: string;
  ofertaId?: string | number;
  show: boolean;
  initialProducts?: ProdutoDetalhado[];
  onValidityChange?: (isValid: boolean) => void;
  initialOffer?: OfferFormState | null;
  initialOfferGroup?: string | null;
}

export const OfferCreationForm = ({
  idsLojas,
  blocosOfertas,
  setOferta,
  editOferta,
  id,
  ofertaId,
  show,
  initialProducts,
  onValidityChange,
  initialOffer,
  initialOfferGroup,
}: OfferCreationFormProps) => {
  const { states, handlers } = useProdutosCampanha({
    idsLojas,
    blocosOfertas: useMemo(() => blocosOfertas, [blocosOfertas]),
    ofertaId,
    show,
    id,
    setOferta,
    initialProducts,
  });

  const { offerForm, offerType: offerTypeSelected, setOfferType, setOfferForm } = useOfferFormContext();

  const {
    handleChangeProduct,
    removeProduct,
    handleSortColumn,
    getProductsTableSorted,
    handleClosePasteModal,
    handleConfirmPasteValues,
    setSearchProduct,
    setPasteModalQuery,
    setBlocoDeOferta,
  } = handlers;

  const { sortColumn, sortType, productsFound, selectedProducts, isPasteModalOpen, pasteModalQuery, blocoDeOferta, loading } = states;

  const hasInitializedOfferForm = useRef(false);
  const offerGroupInitialized = useRef(false);

  useEffect(() => {
    if (!show) {
      hasInitializedOfferForm.current = false;
      offerGroupInitialized.current = false;
    }
  }, [show]);

  useEffect(() => {
    const hasProducts = selectedProducts.value.length > 0;
    const { isValid } = validateOffer(offerTypeSelected, offerForm);
    onValidityChange?.(hasProducts && isValid);
  }, [selectedProducts.value, offerTypeSelected, offerForm, onValidityChange]);

  useEffect(() => {
    if (!editOferta || !initialOffer || hasInitializedOfferForm.current) return;
    if (!initialOffer.offer_type) return;

    hasInitializedOfferForm.current = true;
    setOfferType(initialOffer.offer_type as string);
    setOfferForm(initialOffer as OfferFormState);
  }, [editOferta, initialOffer]);

  useEffect(() => {
    if (!editOferta || offerGroupInitialized.current) return;
    if (!initialOfferGroup || !blocosOfertas || blocosOfertas.length === 0) {
      offerGroupInitialized.current = true;
      setBlocoDeOferta(null);
      return;
    }

    const matchingBlock = blocosOfertas.find(block => block.label === initialOfferGroup);
    if (matchingBlock) {
      offerGroupInitialized.current = true;
      setBlocoDeOferta(matchingBlock);
    }
  }, [editOferta, initialOfferGroup, blocosOfertas]);

  const handleBlocoDeOfertaChange = (value: string | number) => {
    const selectedBlock = blocosOfertas?.find(block => String(block.value) === String(value));
    if (selectedBlock) {
      setBlocoDeOferta(selectedBlock);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Seção 1: Busca de Produtos */}
        <ProductSearchSection
          productsFound={productsFound}
          selectedProductIds={selectedProducts.value}
          loading={loading}
          disabled={editOferta}
          onChangeProduct={handleChangeProduct}
          onSearch={setSearchProduct}
        />

        {selectedProducts.value.length > 0 ? (
          <>
            <ProductsTableSection
              data={getProductsTableSorted()}
              idsLojas={idsLojas}
              sortColumn={sortColumn}
              sortType={sortType}
              onSortColumn={handleSortColumn}
              onRemoveProduct={removeProduct}
            />

            <OfferSettingsSection offerBlocks={blocosOfertas as any} selectedBlockValue={blocoDeOferta?.value} onBlockChange={handleBlocoDeOfertaChange} />
          </>
        ) : (
          <div className={styles.emptyState}>
            <p>Não há produtos selecionados</p>
          </div>
        )}
      </div>

      {/* Modal de Paste */}
      <FilterPasteModal
        show={isPasteModalOpen}
        query={pasteModalQuery}
        onHide={handleClosePasteModal}
        onSearch={setPasteModalQuery}
        onConfirm={handleConfirmPasteValues}
        modalText="Escreva ou cole uma lista de códigos de produto"
      />
    </div>
  );
};

OfferCreationForm.displayName = 'OfferCreationForm';
